import React from "react";
import { fmtPm2Uptime } from "../../utils/format";

export default function Pm2View({ pm2Servers, loadPm2, onAction, onLogs, canOperate, searchTerm = "" }) {
  
  const term = searchTerm.trim().toLowerCase();
  
  const filteredPm2Servers = pm2Servers.map((srv) => {
    const procs = (srv.processes || []).filter((p) => {
      if (!term) return true;
      return (
        (srv.serverCode || "").toLowerCase().includes(term) ||
        (p.name || "").toLowerCase().includes(term)
      );
    });
    return { ...srv, processes: procs };
  }).filter(srv => {
    if (!term) return true;
    return (
      (srv.serverCode || "").toLowerCase().includes(term) ||
      srv.processes.length > 0
    );
  });

  // Tính tổng số process online/tổng số
  let totalProcs = 0;
  let onlineProcs = 0;
  filteredPm2Servers.forEach(s => {
    const list = s.processes || [];
    totalProcs += list.length;
    onlineProcs += list.filter(p => p.status === "online").length;
  });

  return (
    <div>
      {/* Top Header & Summary */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px",
        flexWrap: "wrap",
        gap: "10px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "var(--dim)" }}>
            PM2-managed Node processes
          </span>
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: "12px",
            color: "var(--green)",
            padding: "3px 10px",
            borderRadius: "8px",
            background: "var(--green-soft)",
            fontWeight: 700,
          }}>
            {onlineProcs} / {totalProcs} online
          </span>
        </div>
        
        <button
          onClick={loadPm2}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "7px",
            padding: "0 15px",
            height: "38px",
            borderRadius: "10px",
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            color: "var(--dim)",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          className="pm2-refresh-btn"
          title="Làm mới danh sách PM2"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 2v6h-6"></path>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
            <path d="M3 22v-6h6"></path>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
          </svg>
          Refresh
        </button>
      </div>

      {/* Servers Groups */}
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {filteredPm2Servers.length === 0 ? (
          <div className="empty-state">
            Không tìm thấy tiến trình PM2 nào phù hợp.
            <br />
            <span style={{ fontSize: 11, marginTop: 4 }}>
              Thử tìm kiếm với từ khóa khác hoặc kiểm tra agent.
            </span>
          </div>
        ) : (
          filteredPm2Servers.map((srv) => {
            const procs = Array.isArray(srv.processes) ? srv.processes : [];
            const onlineCount = procs.filter((p) => p.status === "online").length;
            const isConn = srv.isConnected;
            const statusDotColor = isConn ? (onlineCount === procs.length ? "var(--green)" : "var(--amber)") : "var(--faint)";
            const statusDotShadow = isConn ? `0 0 6px ${statusDotColor}` : "none";

            return (
              <div key={srv.serverCode} style={{ marginBottom: "8px" }}>
                {/* Server Header */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "11px 16px",
                  background: "var(--surface2)",
                  border: "1px solid var(--border)",
                  borderRadius: "12px 12px 0 0",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                    <span style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: statusDotColor,
                      boxShadow: statusDotShadow,
                    }}></span>
                    <span style={{ fontSize: "13.5px", fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                      {srv.serverCode}
                    </span>
                    <span style={{ fontSize: "11.5px", color: "var(--faint)" }}>
                      · {srv.IP || "Connected Agent"}
                    </span>
                  </div>
                  <span style={{ fontSize: "11.5px", color: isConn && onlineCount < procs.length ? "var(--amber)" : "var(--faint)", fontFamily: "var(--font-mono)" }}>
                    {onlineCount} / {procs.length} online
                  </span>
                </div>

                {/* Processes List Container */}
                <div style={{
                  border: "1px solid var(--border)",
                  borderTop: "none",
                  borderRadius: "0 0 12px 12px",
                  overflow: "hidden",
                  background: "var(--surface)",
                }}>
                  {procs.length === 0 ? (
                    <div style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "var(--faint)",
                      fontSize: "12.5px",
                      fontFamily: "var(--font-mono)",
                    }}>
                      Không có tiến trình PM2 nào.
                    </div>
                  ) : (
                    procs.map((p, idx) => {
                      const isOnline = p.status === "online";
                      const statusColor = isOnline ? "var(--green)" : p.status === "errored" ? "var(--red)" : "var(--amber)";
                      const statusSoftBg = isOnline ? "var(--green-soft)" : p.status === "errored" ? "var(--red-soft)" : "var(--amber-soft)";

                      return (
                        <div
                          key={p.name}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            padding: "14px 16px",
                            borderBottom: idx === procs.length - 1 ? "none" : "1px solid var(--border)",
                            flexWrap: "wrap",
                          }}
                          className="pm2-row-hover"
                        >
                          {/* Name & Status */}
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: "160px" }}>
                            <span style={{
                              width: "9px",
                              height: "9px",
                              borderRadius: "50%",
                              background: statusColor,
                            }}></span>
                            <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--text)" }}>{p.name}</span>
                            <span style={{
                              fontFamily: "var(--font-mono)",
                              fontSize: "10.5px",
                              fontWeight: 700,
                              color: statusColor,
                              padding: "2px 8px",
                              borderRadius: "6px",
                              background: statusSoftBg,
                            }}>
                              {p.status}
                            </span>
                          </div>

                          {/* Meta stats */}
                          <div style={{
                            display: "flex",
                            gap: "18px",
                            flex: 1,
                            flexWrap: "wrap",
                            fontFamily: "var(--font-mono)",
                            fontSize: "12px",
                          }}>
                            <span style={{ color: "var(--faint)" }}>
                              PID <b style={{ color: "var(--dim)" }}>{p.pid || "—"}</b>
                            </span>
                            <span style={{ color: "var(--faint)" }}>
                              CPU <b style={{ color: isOnline && p.cpuPct > 55 ? "var(--amber)" : isOnline ? "var(--green)" : "var(--faint)" }}>
                                {isOnline ? `${p.cpuPct}%` : "—"}
                              </b>
                            </span>
                            <span style={{ color: "var(--faint)" }}>
                              RAM <b style={{ color: "var(--dim)" }}>
                                {isOnline && p.memMB ? `${Math.round(p.memMB)} MB` : "—"}
                              </b>
                            </span>
                            <span style={{ color: "var(--faint)" }}>
                              Uptime <b style={{ color: "var(--dim)" }}>
                                {fmtPm2Uptime(p.uptime, p.status)}
                              </b>
                            </span>
                            <span style={{ color: "var(--faint)" }}>
                              ↺ <b style={{ color: "var(--dim)" }}>{p.restarts}</b>
                            </span>
                          </div>

                          {/* Control Buttons */}
                          <div style={{ display: "flex", gap: "6px" }}>
                            {/* Logs Console */}
                            <button
                              onClick={() => onLogs(srv.serverCode, p.name)}
                              style={actionBtnSty}
                              className="pm2-act-btn"
                              title="Xem Console Log"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="16" rx="2"></rect>
                                <path d="m7 9 3 3-3 3M13 15h4"></path>
                              </svg>
                              Console
                            </button>

                            {canOperate && (
                              <>
                                {/* Restart */}
                                <button
                                  onClick={() => onAction(srv.serverCode, p.name, "restart")}
                                  style={actionBtnSty}
                                  className="pm2-act-btn-warn"
                                >
                                  Restart
                                </button>
                                
                                {/* Start / Stop */}
                                {isOnline ? (
                                  <button
                                    onClick={() => onAction(srv.serverCode, p.name, "stop")}
                                    style={actionBtnSty}
                                    className="pm2-act-btn-danger"
                                  >
                                    Stop
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => onAction(srv.serverCode, p.name, "start")}
                                    style={actionBtnSty}
                                    className="pm2-act-btn-success"
                                  >
                                    Start
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        .pm2-refresh-btn:hover {
          color: var(--text) !important;
          border-color: var(--border2) !important;
        }
        .pm2-row-hover:hover {
          background: var(--hover) !important;
        }
        .pm2-act-btn {
          padding: 7px 12px;
          border-radius: 9px;
          background: var(--surface2);
          border: 1px solid var(--border2);
          color: var(--dim);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        .pm2-act-btn:hover {
          color: var(--primary) !important;
          border-color: var(--primary) !important;
        }
        .pm2-act-btn-warn {
          padding: 7px 12px;
          border-radius: 9px;
          background: var(--surface2);
          border: 1px solid var(--border2);
          color: var(--text);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .pm2-act-btn-warn:hover {
          color: var(--amber) !important;
          border-color: var(--amber) !important;
        }
        .pm2-act-btn-danger {
          padding: 7px 12px;
          border-radius: 9px;
          background: var(--surface2);
          border: 1px solid var(--border2);
          color: var(--text);
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .pm2-act-btn-danger:hover {
          color: var(--red) !important;
          border-color: var(--red) !important;
        }
        .pm2-act-btn-success {
          padding: 7px 12px;
          border-radius: 9px;
          background: var(--green-soft);
          border: 1px solid var(--green-soft);
          color: var(--green);
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .pm2-act-btn-success:hover {
          filter: brightness(1.07);
        }
      `}</style>
    </div>
  );
}

const actionBtnSty = {
  // Styles base cho các button
};
